// "View record" overlay for a completed onboarding item, matching navigator_people_v83.html (`Xa`).
import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, Image, ActivityIndicator, Platform, Pressable, Linking } from "react-native";
import { useSelector } from "react-redux";
import FontFamily from "../../../../constants/fonts";
import { MyTeamMember } from "../../types";
import { OnboardingChecklistRow } from "../types";
import { fetchOnboardingItemRecord, fetchOnboardingFormQuestions } from "../../peopleHubApi";
import { formatUserName } from "../../../../utils/emailTouserName";
import { formatShortDate } from "../../formatDate";
import {
  DocumentModal,
  DocumentDetailsCard,
  DocumentSection,
  QuestionChecklist,
  ChecklistGroup,
  DocColors,
} from "./OnboardingDocument";

interface ViewOnboardingRecordModalProps {
  visible: boolean;
  onClose: () => void;
  item: OnboardingChecklistRow | null;
  member: MyTeamMember;
}

// Same placeholder target the completion form opens for the external resource.
const FALLBACK_RESOURCE_URL = "https://www.pepsico.com";

const SignatureCard: React.FC<{ role: string; name: string; imageData?: string; signedOn?: string }> = ({
  role,
  name,
  imageData,
  signedOn,
}) => (
  <View style={[styles.sigCard, !imageData && styles.sigCardMissing]}>
    <Text style={styles.sigRole}>{role}</Text>
    {imageData ? (
      <Image source={{ uri: imageData }} style={styles.sigImage} resizeMode="contain" />
    ) : (
      <Text style={styles.sigName}>{name}</Text>
    )}
    <Text style={styles.sigSmall}>{signedOn ? `Signed ${signedOn}` : imageData ? name : "No signature stored."}</Text>
  </View>
);

const ViewOnboardingRecordModal: React.FC<ViewOnboardingRecordModalProps> = ({
  visible,
  onClose,
  item,
  member,
}) => {
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const [record, setRecord] = useState<Record<string, any> | null>(null);
  const [formSections, setFormSections] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const itemKey = item?.itemKey || item?.id || "";
  const isPitCert = itemKey.includes("pit-certification");
  const isPreferredMethod = itemKey.includes("preferred-method");
  const isMilestone = item?.type === "Milestone" || itemKey.includes("milestone");
  const hasQuestionForm = isMilestone || isPreferredMethod;

  useEffect(() => {
    if (!visible || !item) return;

    // Prob_Form: 30 = Milestone 1, 60 = Milestone 2, 90 = Milestone 3. Preferred-method has no probation form.
    let probForm: "30" | "60" | "90" | undefined = "30";
    if (isPreferredMethod) probForm = undefined;
    else if (itemKey.includes("milestone2") || item.phase === "Milestone 2") probForm = "60";
    else if (itemKey.includes("milestone3") || item.phase === "Milestone 3") probForm = "90";

    setIsLoading(true);
    setFormSections([]);
    const questionsRequest = hasQuestionForm
      ? fetchOnboardingFormQuestions({
          probForm,
          locationType: selectedLocation?.locationType,
          organizationType: selectedLocation?.organizationType,
          is3pl: selectedLocation?.is3pl,
        }).catch((err) => {
          console.warn("Could not load form questions", err);
          return null;
        })
      : Promise.resolve(null);

    Promise.all([fetchOnboardingItemRecord(member.gpid, itemKey), questionsRequest])
      .then(([res, questions]) => {
        setRecord(res);
        setFormSections(questions?.sections ?? []);
      })
      .catch((err) => {
        console.warn("Could not load completion record", err);
        // Fallback to item data
        setRecord({
          employee: member.name,
          document: item.activity,
          completedOn: item.completedOn || item.due,
          recordedBy: formatUserName(item.completedBy) || "Supervisor",
          completionType: item.completionType || item.type,
          resourceReviewed: true,
        });
      })
      .finally(() => setIsLoading(false));
  }, [
    visible, item, itemKey, member, hasQuestionForm, isPreferredMethod,
    selectedLocation?.locationType, selectedLocation?.organizationType, selectedLocation?.is3pl,
  ]);

  if (!item) return null;

  const handlePrint = () => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.print();
    }
  };

  const parsedFormAnswers = () => {
    if (!record?.formAnswers) return [];
    try {
      if (typeof record.formAnswers === "string") {
        return JSON.parse(record.formAnswers);
      }
      return record.formAnswers;
    } catch {
      return [];
    }
  };

  const formAnswersList = parsedFormAnswers();

  // Map saved selections (questionId -> answer) onto the configured form-questions.
  const savedAnswers: Record<string, string> = {};
  formAnswersList.forEach((fa: any) => {
    if (fa?.questionId != null && fa.answer != null) savedAnswers[String(fa.questionId)] = fa.answer;
  });
  // The record API also returns the saved selections inside milestoneForm; use them when formAnswers is absent.
  (record?.milestoneForm ?? []).forEach((sec: any) => {
    (sec.questions ?? []).forEach((q: any) => {
      const key = String(q.id);
      if (savedAnswers[key] == null && q.savedAnswer != null) savedAnswers[key] = q.savedAnswer;
    });
  });

  // Prefer form-questions for structure; fall back to the form embedded in the record response.
  const sectionsToRender: any[] = formSections.length > 0 ? formSections : record?.milestoneForm ?? [];
  const checklistGroups: ChecklistGroup[] = sectionsToRender.map((sec: any, secIdx: number) => ({
    key: String(sec.sectionId ?? secIdx),
    title: sec.sectionLabel,
    rows: (sec.questions || []).map((q: any) => ({
      id: String(q.id),
      label: q.text,
      options:
        q.answerType === "ABOVE_ON_BELOW_NA"
          ? ["Above Target/On Target", "Below Target", "N/A"]
          : ["Yes", "No", "N/A"],
    })),
  }));
  const hasQuestionRows = checklistGroups.some((g) => g.rows.length > 0);
  const questionsTitle = record?.milestoneFormLabel || (isPreferredMethod ? "Touchpoint questions" : `${item.phase} review questions`);

  const employeeName = record?.employee || member.name;
  const managerName = formatUserName(record?.recordedBy) || item.completedBy || "Supervisor";
  const resourceUrl = record?.resourceUrl || item.resourceUrl || FALLBACK_RESOURCE_URL;

  // Milestone comments are stored as a JSON map of section comments, which isn't a note to display.
  const rawNotes = record?.notes ?? record?.completionNotes ?? record?.comments;
  const notes = typeof rawNotes === "string" && !rawNotes.trim().startsWith("{") ? rawNotes.trim() : "";
  const showCompletionNotes = !!notes || !!record?.evidence;

  return (
    <DocumentModal
      visible={visible}
      title={`${item.activity}: completed record`}
      onClose={onClose}
      width={900}
      secondaryAction={{ text: "Close", onPress: onClose }}
      primaryAction={isPitCert ? { text: "Print PIT License", onPress: handlePrint } : undefined}
    >
      {/* .people-completed-banner */}
      <View style={styles.banner}>
        <Text style={styles.bannerTitle}>Completed onboarding document</Text>
        <Text style={styles.bannerText}>
          This record is read only and shows the information captured at completion.
        </Text>
      </View>

      {isLoading ? (
        <ActivityIndicator size="large" color={DocColors.blue700} style={styles.loader} />
      ) : (
        <>
          <DocumentDetailsCard
            items={[
              { label: "Employee", value: employeeName },
              { label: "Document", value: record?.document || item.activity },
              { label: "Completed on", value: formatShortDate(record?.completedOn || item.completedOn) || "—" },
              { label: "Recorded by", value: managerName },
            ]}
          />

          {/* .people-document-resource.is-readonly */}
          {!!record?.resourceReviewed && (
            <View style={styles.resource}>
              <View style={styles.resourceCopy}>
                <Text style={styles.resourceTitle}>External resource</Text>
                <Text style={styles.resourceText}>Resource review acknowledged</Text>
              </View>
              <Pressable
                style={styles.resourceLink}
                onPress={() => Linking.openURL(resourceUrl).catch(() => {})}
                accessibilityRole="link"
              >
                <Text style={styles.resourceLinkText}>Open resource</Text>
              </Pressable>
            </View>
          )}

          {/* Read-only form questions with the saved selections */}
          {hasQuestionForm && hasQuestionRows && (
            <QuestionChecklist title={questionsTitle} groups={checklistGroups} answers={savedAnswers} readOnly />
          )}

          {/* Fallback: raw answers when the question set is unavailable */}
          {!(hasQuestionForm && hasQuestionRows) && formAnswersList.length > 0 && (
            <DocumentSection title="Questionnaire Responses">
              {formAnswersList.map((fa: any, idx: number) => (
                <View key={idx} style={styles.answerRow}>
                  <Text style={styles.answerKey}>Question #{fa.questionId || idx + 1}</Text>
                  <View style={styles.answerPill}>
                    <Text style={styles.answerPillText}>{fa.answer}</Text>
                  </View>
                </View>
              ))}
            </DocumentSection>
          )}

          {showCompletionNotes && (
            <DocumentSection title="Completion notes" hint={record?.evidence}>
              {!!notes && <Text style={styles.notes}>{notes}</Text>}
            </DocumentSection>
          )}

          {/* .people-signature-grid */}
          <View style={styles.sigGrid}>
            <SignatureCard
              role="Employee"
              name={employeeName}
              imageData={record?.employeeSignature}
              signedOn={record?.employeeSignedOn}
            />
            {!!record?.managerSignature && (
              <SignatureCard
                role="Manager"
                name={managerName}
                imageData={record.managerSignature}
                signedOn={record?.managerSignedOn}
              />
            )}
          </View>
        </>
      )}
    </DocumentModal>
  );
};

const styles = StyleSheet.create({
  loader: {
    padding: 30,
  },
  // .people-completed-banner
  banner: {
    borderWidth: 1,
    borderColor: DocColors.green200,
    backgroundColor: DocColors.green50,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  bannerTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.greenText,
  },
  bannerText: {
    marginTop: 2,
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.greenText,
  },
  // .people-document-resource
  resource: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    borderWidth: 1,
    borderColor: DocColors.blue200,
    backgroundColor: DocColors.blue25,
    borderRadius: 10,
    padding: 16,
  },
  resourceCopy: {
    flex: 1,
    minWidth: 220,
  },
  resourceTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.blue900,
  },
  resourceText: {
    marginTop: 3,
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
  resourceLink: {
    minHeight: 40,
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 24,
    backgroundColor: DocColors.blue800,
    alignItems: "center",
    justifyContent: "center",
  },
  resourceLinkText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textOnDark,
  },
  // .people-section-copy
  notes: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textSecondary,
  },
  answerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: DocColors.gray25,
  },
  answerKey: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textPrimary,
  },
  answerPill: {
    backgroundColor: DocColors.blue50,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  answerPillText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: DocColors.blue900,
  },
  // .people-signature-grid / .people-signature-complete
  sigGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  sigCard: {
    flexGrow: 1,
    flexBasis: "48%",
    minWidth: 240,
    borderWidth: 1,
    borderColor: DocColors.green200,
    backgroundColor: DocColors.green50,
    borderRadius: 10,
    padding: 14,
  },
  sigCardMissing: {
    borderColor: DocColors.orange200,
    backgroundColor: DocColors.orange75,
  },
  sigRole: {
    fontFamily: FontFamily.bold,
    fontSize: 10,
    lineHeight: 14,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: DocColors.textSecondary,
  },
  sigImage: {
    width: "100%",
    maxWidth: 320,
    height: 74,
    marginTop: 6,
  },
  sigName: {
    marginTop: 10,
    fontFamily: FontFamily.bold,
    fontStyle: "italic",
    fontSize: 24,
    lineHeight: 32,
    color: DocColors.blue900,
  },
  sigSmall: {
    marginTop: 4,
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
});

export default ViewOnboardingRecordModal;
